import { useCallback, useMemo, useState } from "react"

import { differenceInMinutes, format } from "date-fns"
import { toast } from "sonner"

import { validateAdHocStop } from "@/lib/adHocStopValidation"
import { resolveStopTimes } from "@/lib/stagedStops"
import {
  Location,
  Route,
  RouteInstance,
  SlotService,
  Stop,
  slotTypeToStopType,
} from "@/types/collections"

import type { PendingStopAdd } from "./useStagedRouteEdits"

export type AdHocStopType =
  | "shipper_collection"
  | "internal_stop"
  | "haulage_collection"
  | "haulage_delivery"
  | "depot_injection"
  | "direct_injection"
  | "charging_stop"

export interface AdHocStopForm {
  locationId: string
  routeId: string
  adHocStopType: AdHocStopType
  stopDate: string
  arrivalTime: string
  departureTime: string
  slotServices: SlotService[]
  slotPnl: string[]
}

/** A direct injection needs them too: the forecast sync matches the shipper on them. */
export const SERVICE_CONFIGURED_STOP_TYPES: AdHocStopType[] = [
  "shipper_collection",
  "haulage_collection",
  "haulage_delivery",
  "direct_injection",
]

interface AdHocStopFormOptions {
  dateStr: string
  routes: Route[]
  locations: Location[]
  routeInstancesData: RouteInstance[] | undefined
  isPastDate: boolean
  nextPendingStopId: () => string
  stageStopAdd: (route: Route, stagedStop: Stop, pending: PendingStopAdd) => Promise<void>
  onStaged: (route: Route, arrivalTime: Date) => void
}

/**
 * Not reset between openings: only the drag's route and times are replaced, so other choices carry
 * over.
 */
export function useAdHocStopForm({
  dateStr,
  routes,
  locations,
  routeInstancesData,
  isPastDate,
  nextPendingStopId,
  stageStopAdd,
  onStaged,
}: AdHocStopFormOptions) {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<AdHocStopForm>({
    locationId: "",
    routeId: "",
    adHocStopType: "shipper_collection",
    stopDate: dateStr,
    arrivalTime: "08:00",
    departureTime: "08:30",
    slotServices: [],
    slotPnl: [],
  })
  const [error, setError] = useState<string | null>(null)
  const [locationSearchTerm, setLocationSearchTerm] = useState("")

  const openForDrawnStop = useCallback((route: Route, startTime: Date, endTime: Date) => {
    setForm((prev) => ({
      ...prev,
      routeId: route.id,
      stopDate: format(startTime, "yyyy-MM-dd"),
      arrivalTime: format(startTime, "HH:mm"),
      departureTime: format(endTime, "HH:mm"),
    }))
    setError(null)
    setLocationSearchTerm("")
    setOpen(true)
  }, [])

  const selectedRoute = useMemo(() => {
    if (!form.routeId) return null
    return routes.find((r) => r.id === form.routeId) || null
  }, [form.routeId, routes])

  // A drag can land on the day after; resolve times against the form's stopDate, not selectedDate.
  const times = useMemo(
    () => resolveStopTimes(form.stopDate, form.arrivalTime, form.departureTime),
    [form.stopDate, form.arrivalTime, form.departureTime]
  )

  const serviceTime = useMemo(
    () => Math.max(0, differenceInMinutes(times.departure, times.arrival)),
    [times]
  )

  const validation = useMemo(
    () =>
      validateAdHocStop(
        { routeId: form.routeId, locationId: form.locationId },
        routes,
        times,
        new Date()
      ),
    [form.routeId, form.locationId, times, routes]
  )

  const submit = async () => {
    if (!validation.isValid) {
      setError(validation.error)
      return
    }

    if (!form.routeId || !form.locationId) return

    const location = locations.find((l) => l.id === form.locationId)
    if (!location) return

    const route = routes.find((r) => r.id === form.routeId)
    if (!route) return

    const routeInstance = routeInstancesData?.find((ri: RouteInstance) => ri.id === form.routeId)
    if (!routeInstance) return

    if (isPastDate) {
      toast.error("Cannot edit past dates", {
        description: "Ad-hoc stops can only be added for today's date. Past dates are read-only.",
      })
      setOpen(false)
      return
    }

    const { arrival: arrivalTime, departure: departureTime } = times

    const routeDate = new Date(routeInstance.date)
    routeDate.setHours(0, 0, 0, 0)

    try {
      const tempId = nextPendingStopId()

      const stagedStop: Stop = {
        id: tempId,
        routeId: route.id,
        sequence: route.stops.length + 1,
        type: slotTypeToStopType[form.adHocStopType] || "collection",
        name: location.site_name,
        address: location.address,
        postcode: location.postcode,
        lat: location.latitude,
        lng: location.longitude,
        plannedArrival: arrivalTime,
        plannedDeparture: departureTime,
        estimatedArrival: arrivalTime,
        estimatedDeparture: departureTime,
        status: "scheduled",
        serviceTimeMinutes: serviceTime,
        volumeEstimated: 0,
        locationId: location.id,
        slotType: form.adHocStopType,
        comments: [],
      }

      await stageStopAdd(route, stagedStop, {
        tempId,
        routeId: route.id,
        routeDate,
        stopData: {
          name: location.site_name,
          address: location.address,
          postcode: location.postcode,
          lat: location.latitude,
          lng: location.longitude,
          location_id: location.id,
          stop_type: form.adHocStopType,
          // Times, service time and sequence are re-read off the staged stop on save, so a later
          // drag carries through.
          planned_arrival: arrivalTime.toISOString(),
          planned_departure: departureTime.toISOString(),
          service_time_minutes: serviceTime,
          slot_services: form.slotServices.length > 0 ? form.slotServices : undefined,
          slot_pnl: form.slotPnl.length > 0 ? form.slotPnl : undefined,
        },
      })

      toast.success("Stop added", {
        description: `Ad-hoc stop at ${location.site_name} is pending on route ${route.code}. Save changes to write it and sync with GPS.`,
      })

      setOpen(false)
      setError(null)

      onStaged(route, arrivalTime)
    } catch (error) {
      console.error("Error staging ad-hoc stop:", error)
      toast.error("Error adding stop", {
        description: "Failed to add the ad-hoc stop. Please try again.",
      })
    }
  }

  const needsServiceConfig = SERVICE_CONFIGURED_STOP_TYPES.includes(form.adHocStopType)
  const canSubmit =
    !!form.routeId &&
    !!form.locationId &&
    validation.isValid &&
    !(needsServiceConfig && (form.slotServices.length === 0 || form.slotPnl.length === 0))

  return {
    open,
    setOpen,
    form,
    setForm,
    error: validation.error || error,
    locationSearchTerm,
    setLocationSearchTerm,
    selectedRoute,
    serviceTime,
    needsServiceConfig,
    canSubmit,
    openForDrawnStop,
    submit,
  }
}
